import { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import WorkTemplatesContext from '@atom/components/workTemplates/WorkTemplatesContext';
import { Icon, IconButton, Tooltip } from '@atom/mui';
import { ArrowDropDownIcon, ArrowRightIcon, FolderIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { WorkTemplateFolderTree } from '@atom/types/workTemplate';

import FolderOptions from './FolderOptions';
import WorkTemplatesFolderRow from './WorkTemplatesFolderRow';

import classes from './workTemplatesFolderTree.module.css';

const styles = {
  indentation: {
    paddingLeft: '2rem',
  },
  iconStyle: {
    fontSize: '24px',
    margin: 0,
  },
  overflowStyle: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    width: '12rem',
  },
};

export interface Props {
  workTemplateFolderTree: WorkTemplateFolderTree;
}

const WorkTemplatesFolderTree = ({ workTemplateFolderTree }: Props) => {
  const {
    open,
    toggleOpen,
    setActiveFolderId,
    activeFolderId,
    setIsActiveFolderLeafNode,
    setWorkTemplatesView,
  } = useContext(WorkTemplatesContext);
  const expanded = open.has(workTemplateFolderTree.id);
  const [overflowing, setOverflowing] = useState<boolean>(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { offsetWidth, scrollWidth } = container.current;
    setOverflowing(offsetWidth < scrollWidth);
  }, [workTemplateFolderTree.name]);

  useEffect(() => {
    if (activeFolderId === workTemplateFolderTree.id) {
      setIsActiveFolderLeafNode(R.isEmpty(workTemplateFolderTree.children));
    }
  }, [
    setIsActiveFolderLeafNode,
    workTemplateFolderTree.children,
    activeFolderId,
    workTemplateFolderTree.id,
  ]);

  const arrowDirection = expanded ? ArrowDropDownIcon : ArrowRightIcon;

  const renderExpandedArrow = !R.isEmpty(workTemplateFolderTree.children);

  const onClick = () => {
    setActiveFolderId(workTemplateFolderTree.id);
    setIsActiveFolderLeafNode(R.isEmpty(workTemplateFolderTree.children));
    setWorkTemplatesView({ page: 1 });
  };

  return (
    <>
      <div
        className={clsx(
          classes.folderRow,
          workTemplateFolderTree.id === activeFolderId && classes.selected,
        )}
        onClick={onClick}
      >
        <div
          ref={container}
          className={classes.folderNameContainer}
          style={renderExpandedArrow ? {} : styles.indentation}
        >
          {renderExpandedArrow && (
            <IconButton
              onClick={() => toggleOpen(workTemplateFolderTree.id)}
              style={styles.iconStyle}
            >
              <Icon color={colors.neutral.dim} icon={arrowDirection} />
            </IconButton>
          )}
          <Icon
            style={styles.iconStyle}
            color={colors.neutral.gray}
            icon={FolderIcon}
          />
          <div className={classes.rowNameContainer}>
            <div>
              {!overflowing ? (
                workTemplateFolderTree.name
              ) : (
                <Tooltip
                  lightTooltip
                  title={workTemplateFolderTree.name}
                  placement="right-start"
                >
                  <p style={styles.overflowStyle}>
                    {workTemplateFolderTree.name}
                  </p>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        <FolderOptions folder={workTemplateFolderTree} isRootFolder />
      </div>
      {expanded ? (
        <>
          {workTemplateFolderTree.children.map(
            (child: WorkTemplateFolderTree) => (
              <WorkTemplatesFolderRow
                key={child.id}
                folder={child}
                indentation={0}
              />
            ),
          )}
        </>
      ) : (
        <div />
      )}
    </>
  );
};

export default WorkTemplatesFolderTree;
